import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { ethers } from 'ethers';
import { getADNFTContract, getUSDTContract } from '../web3/contractInstance';
import { makeReadProvider } from '../web3/providerHelper';
import { useWallet } from './WalletContext';
import { APP } from '../config/appConfig';

const ContractContext = createContext(null);

const ZERO = ethers.BigNumber.from(0);

const EMPTY_STATE = {
  loaded: false,
  loading: false,
  userId: 0,
  basic: null,
  extended: null,
  investments: [],
  pendingROI: ZERO,
  levelCounts: [],
  levelVolumes: [],
  levelEligibility: [],
  activities: [],
  planInfo: null,
  limits: null,
  fees: null,
  usdtBalance: ZERO,
  usdtAllowance: ZERO,
  contractStats: null,
  incomeBreakdown: null,     // { roi, levelIncome, totalEarnedOut, pending, claimed }
  claimInfo: null,           // { pending, minClaim, feeBps, maxClaimable, netIfClaimAll }
  directBusiness: ZERO,
  teamBusiness: ZERO,
  teamCount: 0,
  activeDirects: 0,
  ceilingTotal: ZERO,
  earnedTotal: ZERO,
  ownerAddress: null,
  adminAddress: null,
  isOwner: false,
  isAdmin: false,
  tierConfig: null,          // { directMinInvestment, levelsPerQualifyingDirect }
  unlockInfo: null,          // { qualifyingDirects, naturalUnlocked, adminOverride, effectiveUnlocked }
  adminStats: null           // { totalWithdraw, availableWithdraw, totalInvestment, todayInvestment }
};

// Cap heavy per-direct enumeration so a huge tree doesn't stall the UI.
const MAX_DIRECTS_TO_ENUM = 40;

export function ContractProvider({ children }) {
  const { account, isCorrectNetwork, getEthersProvider } = useWallet();
  const [state, setState] = useState(EMPTY_STATE);
  const pollingRef = useRef(null);
  const inFlightRef = useRef(false);

  async function refresh() {
    if (!account || !isCorrectNetwork) {
      setState(EMPTY_STATE);
      return;
    }
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    setState(prev => ({ ...prev, loading: true }));
    try {
      const ethersProvider = getEthersProvider();
      const provider = ethersProvider ? ethersProvider : makeReadProvider();
      const c = getADNFTContract(provider);
      const usdt = getUSDTContract(provider);

      const userId = await c.addressToId(account);
      const uid = userId.toNumber ? userId.toNumber() : Number(userId);

      const [planInfo, limits, fees, contractStats, usdtBalance, usdtAllowance, ownerAddress, adminAddress, dMin, lPerDir, adminStatsRaw] = await Promise.all([
        c.getPlanInfo().catch(() => null),
        c.getInvestmentLimits().catch(() => null),
        c.getFees().catch(() => null),
        c.getContractStats().catch(() => null),
        usdt.balanceOf(account).catch(() => ZERO),
        usdt.allowance(account, c.address).catch(() => ZERO),
        c.owner().catch(() => null),
        c.admin().catch(() => null),
        c.directMinInvestment().catch(() => null),
        c.levelsPerQualifyingDirect().catch(() => null),
        c.getAdminStats().catch(() => null)
      ]);
      const tierConfig = {
        directMinInvestment: dMin,
        levelsPerQualifyingDirect: lPerDir ? (lPerDir.toNumber ? lPerDir.toNumber() : Number(lPerDir)) : 3
      };
      const adminStats = adminStatsRaw && {
        totalWithdraw:     adminStatsRaw.totalWithdraw     || adminStatsRaw[0],
        availableWithdraw: adminStatsRaw.availableWithdraw || adminStatsRaw[1],
        totalInvestment:   adminStatsRaw.totalInvestment   || adminStatsRaw[2],
        todayInvestment:   adminStatsRaw.todayInvestment   || adminStatsRaw[3]
      };

      const isOwner = !!(ownerAddress && account && ownerAddress.toLowerCase() === account.toLowerCase());
      const isAdmin = !!(adminAddress && account && adminAddress.toLowerCase() === account.toLowerCase());

      const shapedPlan = planInfo && {
        dailyROI: planInfo._dailyROI || planInfo[0],
        ceilingMultiplier: planInfo._ceilingMultiplier || planInfo[1],
        timeframe: planInfo._timeframe || planInfo[2]
      };
      const shapedLimits = limits && {
        min: limits._min || limits[0],
        multiple: limits._multiple || limits[1],
        minClaim: limits._minClaim || limits[2]
      };
      const shapedFees = fees && {
        teamWalletFee: fees._teamWalletFee || fees[0],
        contractShareFee: fees._contractShareFee || fees[1],
        claimFee: fees._claimFee || fees[2]
      };

      if (uid === 0) {
        setState({
          ...EMPTY_STATE,
          loaded: true,
          loading: false,
          userId: 0,
          planInfo: shapedPlan,
          limits: shapedLimits,
          fees: shapedFees,
          contractStats,
          usdtBalance,
          usdtAllowance,
          ownerAddress,
          adminAddress,
          isOwner,
          isAdmin,
          tierConfig,
          unlockInfo: null,
          adminStats
        });
        return;
      }

      const [basic, extended, invDetails, pendingROI, levelCountsArr, levelVolumesArr, levelEligibilityArr, activitiesResult, incomeBd, claimInfoRaw, directIds, unlockRaw] = await Promise.all([
        c.getUserBasicInfo(uid).catch(() => null),
        c.getUserExtendedInfo(uid).catch(() => null),
        c.getUserInvestmentDetails(uid).catch(() => null),
        c.calculatePendingROI(uid).catch(() => ZERO),
        c.getUserLevelCounts(uid).catch(() => []),
        c.getUserLevelVolumes(uid).catch(() => []),
        c.getUserLevelEligibility(uid).catch(() => []),
        c.getUserActivities(uid, 0, 50).catch(() => [[], ZERO]),
        c.getIncomeBreakdown(uid).catch(() => null),
        c.getClaimInfo(uid).catch(() => null),
        c.getUserDirectReferrals(uid).catch(() => []),
        c.getLevelUnlockInfo(uid).catch(() => null)
      ]);
      const unlockInfo = unlockRaw && {
        qualifyingDirects: (unlockRaw.qualifyingDirects || unlockRaw[0]).toNumber ? (unlockRaw.qualifyingDirects || unlockRaw[0]).toNumber() : Number(unlockRaw.qualifyingDirects || unlockRaw[0]),
        naturalUnlocked: (unlockRaw.naturalUnlocked || unlockRaw[1]).toNumber ? (unlockRaw.naturalUnlocked || unlockRaw[1]).toNumber() : Number(unlockRaw.naturalUnlocked || unlockRaw[1]),
        adminOverride: (unlockRaw.adminOverride || unlockRaw[2]).toNumber ? (unlockRaw.adminOverride || unlockRaw[2]).toNumber() : Number(unlockRaw.adminOverride || unlockRaw[2]),
        effectiveUnlocked: (unlockRaw.effectiveUnlocked || unlockRaw[3]).toNumber ? (unlockRaw.effectiveUnlocked || unlockRaw[3]).toNumber() : Number(unlockRaw.effectiveUnlocked || unlockRaw[3])
      };

      // Investments
      let investments = [];
      let ceilingTotal = ZERO;
      let earnedTotal = ZERO;
      if (invDetails) {
        const amounts = invDetails.amounts || invDetails[0];
        const timestamps = invDetails.timestamps || invDetails[1];
        const roiEarned = invDetails.roiEarned || invDetails[2];
        const levelIncomeEarned = invDetails.levelIncomeEarned || invDetails[3];
        const ceilings = invDetails.ceilings || invDetails[4];
        const active = invDetails.active || invDetails[5];
        const investmentTypes = invDetails.investmentTypes || invDetails[6];
        for (let i = 0; i < amounts.length; i++) {
          const c1 = ceilings[i];
          const e1 = roiEarned[i].add ? roiEarned[i].add(levelIncomeEarned[i]) : ethers.BigNumber.from(roiEarned[i]).add(levelIncomeEarned[i]);
          ceilingTotal = ceilingTotal.add(c1);
          earnedTotal = earnedTotal.add(e1);
          investments.push({
            index: i,
            amount: amounts[i],
            timestamp: (timestamps[i].toNumber ? timestamps[i].toNumber() : Number(timestamps[i])),
            roiEarned: roiEarned[i],
            levelIncomeEarned: levelIncomeEarned[i],
            ceilingAmount: c1,
            active: active[i],
            investmentType: Number(investmentTypes[i])
          });
        }
      }

      const activitiesRaw = activitiesResult && (activitiesResult[0] || activitiesResult);
      const activities = (activitiesRaw || []).map((a) => ({
        timestamp: a.timestamp && a.timestamp.toNumber ? a.timestamp.toNumber() : Number(a.timestamp || 0),
        amount: a.amount,
        activityType: Number(a.activityType)
      })).sort((a, b) => b.timestamp - a.timestamp);

      // Level totals
      const levelCounts = (levelCountsArr || []).map(x => x.toNumber ? x.toNumber() : Number(x));
      const levelVolumes = Array.from(levelVolumesArr || []);
      const teamCount = levelCounts.reduce((a, b) => a + b, 0);

      // Direct + team business
      const directBusiness = levelVolumes[0] || ZERO;
      const teamBusiness = extended ? (extended.teamVolume || extended[1]) : ZERO;

      // Active directs — enumerate direct IDs (capped) and check .active on each
      let activeDirects = 0;
      const directArr = Array.from(directIds || []).map(x => x.toNumber ? x.toNumber() : Number(x));
      const toCheck = directArr.slice(0, MAX_DIRECTS_TO_ENUM);
      const directUsers = await Promise.all(toCheck.map(id => c.users(id).catch(() => null)));
      for (const u of directUsers) {
        if (!u) continue;
        // users() returns tuple; active is the last field (index 9)
        const activeBool = u.active !== undefined ? u.active : u[9];
        if (activeBool) activeDirects++;
      }

      // Shape income breakdown (contract now returns 6 fields incl. directIncome)
      const incomeBreakdown = incomeBd && {
        roi:            incomeBd.roi            || incomeBd[0],
        levelIncome:    incomeBd.levelIncome    || incomeBd[1],
        directIncome:   incomeBd.directIncome   || incomeBd[2],
        totalEarnedOut: incomeBd.totalEarnedOut || incomeBd[3],
        pending:        incomeBd.pending        || incomeBd[4],
        claimed:        incomeBd.claimed        || incomeBd[5]
      };
      const claimInfo = claimInfoRaw && {
        pending: claimInfoRaw.pending || claimInfoRaw[0],
        minClaim: claimInfoRaw.minClaim || claimInfoRaw[1],
        feeBps: claimInfoRaw.feeBps || claimInfoRaw[2],
        maxClaimable: claimInfoRaw.maxClaimable || claimInfoRaw[3],
        netIfClaimAll: claimInfoRaw.netIfClaimAll || claimInfoRaw[4]
      };

      setState({
        loaded: true,
        loading: false,
        userId: uid,
        basic: basic && {
          wallet: basic.wallet || basic[0],
          totalInvestment: basic.totalInvestment || basic[1],
          totalEarned: basic.totalEarned || basic[2],
          pendingRewards: basic.pendingRewards || basic[3],
          lastTradeTime: (basic.lastTradeTime || basic[4]).toNumber
            ? (basic.lastTradeTime || basic[4]).toNumber()
            : Number(basic.lastTradeTime || basic[4]),
          nextTradeEligibleAt: (basic.nextTradeEligibleAt || basic[5]).toNumber
            ? (basic.nextTradeEligibleAt || basic[5]).toNumber()
            : Number(basic.nextTradeEligibleAt || basic[5])
        },
        extended: extended && {
          directReferrals: (extended.directReferrals || extended[0]).toNumber
            ? (extended.directReferrals || extended[0]).toNumber()
            : Number(extended.directReferrals || extended[0]),
          teamVolume: extended.teamVolume || extended[1],
          referrerId: (extended.referrerId || extended[2]).toNumber
            ? (extended.referrerId || extended[2]).toNumber()
            : Number(extended.referrerId || extended[2]),
          active: extended.active !== undefined ? extended.active : extended[3],
          isBlacklisted: extended.isBlacklisted !== undefined ? extended.isBlacklisted : extended[4]
        },
        investments,
        pendingROI,
        levelCounts,
        levelVolumes,
        levelEligibility: Array.from(levelEligibilityArr || []).map(b => !!b),
        activities,
        planInfo: shapedPlan,
        limits: shapedLimits,
        fees: shapedFees,
        contractStats,
        usdtBalance,
        usdtAllowance,
        incomeBreakdown,
        claimInfo,
        directBusiness,
        teamBusiness,
        teamCount,
        activeDirects,
        ceilingTotal,
        earnedTotal,
        ownerAddress,
        adminAddress,
        isOwner,
        isAdmin,
        tierConfig,
        unlockInfo,
        adminStats
      });
    } catch (e) {
      setState(prev => ({ ...prev, loading: false, loaded: true }));
    } finally {
      inFlightRef.current = false;
    }
  }

  useEffect(() => {
    if (!account || !isCorrectNetwork) {
      setState(EMPTY_STATE);
      if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
      return;
    }
    refresh();
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(refresh, APP.pollIntervalMs);
    return () => {
      if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, isCorrectNetwork]);

  const value = { ...state, refresh };
  return <ContractContext.Provider value={value}>{children}</ContractContext.Provider>;
}

export function useContractState() {
  const ctx = useContext(ContractContext);
  if (!ctx) throw new Error('useContractState outside ContractProvider');
  return ctx;
}
